import React, { memo } from 'react';
import PropTypes from 'prop-types';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';
import { getCardImageField } from '../../../helpers/resolveCardImage';
import CardImage from './CardImage';

// Fallback image per portal type (lowercase)
const TYPE_DEFAULT_IMAGES = {
  topic: '/topic_default_image.svg',
  project: '/project_default_image.svg',
  job: '/job_default_image.png',
  thesis: '/thesis_default_image.png',
  course: '/course_default_image.png',
  publication: '/publication_default_image.svg',
  video: '/video_default_image.svg',
  competition: '/competition_default_image.svg',
  robot: '/robot_default_image.svg',
};

const FlexFullwidthCard = memo(
  ({ item, defaultImage, compact, hideLink, gridLayout, imageOverlay }) => {
    const fallback =
      defaultImage ||
      TYPE_DEFAULT_IMAGES[item['@type']?.toLowerCase()] ||
      '/default_image.svg';

    // Card image: always the Lead Image (`image`) - never a
    // teaser_image1/teaser_image2 fallback. Those fields are for the full
    // view only; every content item is expected to carry its own Lead Image
    // (chosen by the editor from the images stored in its Contents), and a
    // missing one shows the type's default image instead.
    // Sponsor is the exception: it has no Lead Image, only a logo.
    const imageField =
      !getCardImageField(item, 'image')?.scales &&
      item['@type']?.toLowerCase() === 'sponsor'
        ? 'logo'
        : 'image';

    const title = item.title ? item.title : item.id;
    const titleEl = <h4 className="cards__title">{title}</h4>;

    return (
      <div
        className={`cards__item ${gridLayout ? 'cards__item--grid' : 'cards__item--fullwidth'}`}
      >
        {!compact && (
          <CardImage
            item={item}
            fallback={fallback}
            field={imageField}
            gridLayout={gridLayout}
            href={hideLink ? undefined : item['@id']}
            overlay={imageOverlay}
            speculative
          />
        )}
        <div className="cards__content">
          {hideLink ? (
            titleEl
          ) : (
            <UniversalLink href={item['@id']}>{titleEl}</UniversalLink>
          )}
          {item.description && (
            <p className="cards__description">{item.description}</p>
          )}
        </div>
      </div>
    );
  },
);

FlexFullwidthCard.displayName = 'FlexFullwidthCard';

FlexFullwidthCard.propTypes = {
  item: PropTypes.shape({
    '@id': PropTypes.string.isRequired,
    '@type': PropTypes.string,
    title: PropTypes.string,
    id: PropTypes.string,
    description: PropTypes.string,
    image_caption: PropTypes.string,
    image_copyright: PropTypes.string,
    image_copyright_black_font: PropTypes.bool,
    image_scales: PropTypes.object,
  }).isRequired,
  defaultImage: PropTypes.string,
  compact: PropTypes.bool,
  hideLink: PropTypes.bool,
  gridLayout: PropTypes.bool,
  // Rendered on top of the card image, inside the (position: relative)
  // image container - used by the Video card for its play badge, so a
  // still frame reads as "this is a video" rather than a photo.
  imageOverlay: PropTypes.node,
};

export default FlexFullwidthCard;
